import { Navigate, Route } from "react-router"
import type { ReactNode } from "react"
import type { Permission } from "@server/permissions"
import { AdminLayoutNew } from "./layout-new"
import { useHasPermission, useIsSuperAdmin } from "./session-context"
import { LoginPage } from "./login"
import { OverviewPage } from "./overview"
import { MembersPage } from "./members"
import { RolesPage } from "./roles"
import { ChildrenPage } from "./children"
import { ChildDetailPage } from "./child-detail"
import { AddChildPage } from "./add-child"
import { NominationsPage } from "./nominations"
import { NominationDetailPage } from "./nomination-detail"
import { SponsorsPage } from "./sponsors"
import { SponsorDetailPage } from "./sponsor-detail"
import { AddSponsorPage } from "./add-sponsor"
import { GivingPage } from "./giving"
import { GivingDetailPage } from "./giving-detail"
import { SchoolsPage } from "./schools"
import { SchoolDetailPage } from "./school-detail"
import { ProgramsPage } from "./programs"
import { ProgramDetailPage } from "./program-detail"
import { AbilityPage } from "./ability"
import { AbilityDetailPage } from "./ability-detail"
import { AddAbilityPage } from "./add-ability"
import { EventsPage } from "./events"
import { EventDetailPage } from "./event-detail"
import { FormsPage } from "./forms"
import { FormBuilderPage } from "./form-builder"
import { FormSubmissionsPage } from "./form-submissions"
import { ContentPage } from "./content"
import { NewslettersPage } from "./newsletters"
import { NewsletterEditorPage } from "./newsletter-editor"
import { SubscribersPage } from "./subscribers"

function PermissionGate({ permission, children }: { permission: Permission; children: ReactNode }) {
  return useHasPermission(permission) ? <>{children}</> : <Navigate to="/admin" replace />
}

function SuperAdminGate({ children }: { children: ReactNode }) {
  return useIsSuperAdmin() ? <>{children}</> : <Navigate to="/admin" replace />
}

export default (
  <>
    {/* Login renders standalone — no admin shell, no sidebar. */}
    <Route path="admin/login" element={<LoginPage />} />
    <Route path="admin" element={<AdminLayoutNew />}>
      <Route index element={<OverviewPage />} />
      <Route path="children" element={<PermissionGate permission="children"><ChildrenPage /></PermissionGate>} />
      <Route path="children/new" element={<PermissionGate permission="children"><AddChildPage /></PermissionGate>} />
      <Route path="children/:id" element={<PermissionGate permission="children"><ChildDetailPage /></PermissionGate>} />
      <Route path="nominations" element={<PermissionGate permission="nominations"><NominationsPage /></PermissionGate>} />
      <Route path="nominations/:id" element={<PermissionGate permission="nominations"><NominationDetailPage /></PermissionGate>} />
      <Route path="sponsors" element={<PermissionGate permission="sponsors"><SponsorsPage /></PermissionGate>} />
      <Route path="sponsors/new" element={<PermissionGate permission="sponsors"><AddSponsorPage /></PermissionGate>} />
      <Route path="sponsors/:id" element={<PermissionGate permission="sponsors"><SponsorDetailPage /></PermissionGate>} />
      <Route path="giving" element={<PermissionGate permission="giving"><GivingPage /></PermissionGate>} />
      <Route path="giving/:id" element={<PermissionGate permission="giving"><GivingDetailPage /></PermissionGate>} />
      <Route path="schools" element={<PermissionGate permission="schools"><SchoolsPage /></PermissionGate>} />
      <Route path="schools/:id" element={<PermissionGate permission="schools"><SchoolDetailPage /></PermissionGate>} />
      <Route path="programs" element={<PermissionGate permission="programs"><ProgramsPage /></PermissionGate>} />
      <Route path="programs/:id" element={<PermissionGate permission="programs"><ProgramDetailPage /></PermissionGate>} />
      <Route path="programs/participants/:id" element={<PermissionGate permission="programs"><ProgramDetailPage /></PermissionGate>} />
      <Route path="ability" element={<PermissionGate permission="ability"><AbilityPage /></PermissionGate>} />
      <Route path="ability/new" element={<PermissionGate permission="ability"><AddAbilityPage /></PermissionGate>} />
      <Route path="ability/:id" element={<PermissionGate permission="ability"><AbilityDetailPage /></PermissionGate>} />
      <Route path="events" element={<PermissionGate permission="events"><EventsPage /></PermissionGate>} />
      <Route path="events/:id" element={<PermissionGate permission="events"><EventDetailPage /></PermissionGate>} />
      <Route path="forms" element={<PermissionGate permission="forms"><FormsPage /></PermissionGate>} />
      <Route path="forms/builder/:id" element={<PermissionGate permission="forms"><FormBuilderPage /></PermissionGate>} />
      <Route path="forms/builder/:id/submissions" element={<PermissionGate permission="forms"><FormSubmissionsPage /></PermissionGate>} />
      <Route path="content" element={<PermissionGate permission="content"><ContentPage /></PermissionGate>} />
      <Route path="newsletters/new" element={<PermissionGate permission="content_mutate"><NewsletterEditorPage /></PermissionGate>} />
      <Route path="newsletters" element={<PermissionGate permission="content"><NewslettersPage /></PermissionGate>} />
      <Route path="newsletters/:id" element={<PermissionGate permission="content"><NewsletterEditorPage /></PermissionGate>} />
      <Route path="subscribers" element={<PermissionGate permission="content"><SubscribersPage /></PermissionGate>} />
      <Route path="members" element={<SuperAdminGate><MembersPage /></SuperAdminGate>} />
      <Route path="roles" element={<SuperAdminGate><RolesPage /></SuperAdminGate>} />
      <Route path="inquiries" element={<PermissionGate permission="forms"><Navigate to="/admin/forms?tab=inquiries" replace /></PermissionGate>} />
      <Route path="applications" element={<PermissionGate permission="forms"><Navigate to="/admin/forms?tab=applications" replace /></PermissionGate>} />
    </Route>
  </>
)
